import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Modal, Tooltip, message } from 'antd';
import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import {
  BankMark,
  CashTypeIcon,
  ConnectedStatus,
  LiabilityMark,
  LinkedAccountsTable,
  linkedAccountFundsLabel,
  linkedAccountTypeLabel,
  linkedAccountsTotals,
} from '@wavvest/wealth-ui';
import type { LinkedAccountRow } from '@wavvest/wealth-ui';
import { deletePlaidConnection } from '@/api/custom';
import type { TimeRange } from '@/api/custom';
import { fetchPlaidWealthSnapshot } from '@/store/features/plaid/plaid-thunks';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import { formatCurrency } from '@wavvest/shared-utils';
import { ASSET_LIABILITY } from '@/util/planning-util';
import { selectPlaidDisplayBalance } from '@/util/plaid-util';
import { formatAccountType } from '@/util/account-type-format';
import {
  BankLinkWallet,
  formatExternalBankAccountTitle,
  fundingChipAccountKind,
} from '@/util/bank-link';
import { getAccountFundingPlaidId } from './deposit-account-selection';
import { requestPlaidLink } from './plaid-link-request';
import type { BankAccountData } from '@/util/plaid-util';

type LinkedAccountsSectionProps = {
  wallets: readonly BankLinkWallet[];
  /** Chart range on screen, so a refresh after unlinking keeps the same range. */
  chartRange?: TimeRange;
};

const isApex = (account: BankAccountData) => account.custodian_code === 'APEX';
const isLiability = (account: BankAccountData) =>
  account.asset_or_liability === ASSET_LIABILITY.LIABILITY;
const isCash = (account: BankAccountData) => !isApex(account) && !isLiability(account);

const accountTitle = (account: BankAccountData) =>
  formatExternalBankAccountTitle({
    institution_name: account.institution_name,
    name: account.name,
    account_type: account.account_type,
  });

const LinkedAccountsSection: React.FC<LinkedAccountsSectionProps> = ({
  wallets,
  chartRange = 'ALL',
}) => {
  const dispatch = useAppDispatch();
  const accounts = useAppSelector(selectPlaidAccounts);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const cash = useMemo(() => accounts.filter(isCash), [accounts]);
  const liabilities = useMemo(() => accounts.filter(isLiability), [accounts]);
  const cashTotal = cash.reduce((sum, account) => sum + selectPlaidDisplayBalance(account), 0);
  const owedTotal = liabilities.reduce(
    (sum, account) => sum + selectPlaidDisplayBalance(account),
    0,
  );

  const fundedCount = (account: BankAccountData) => {
    const plaidId = getAccountFundingPlaidId(account);
    return wallets.filter((wallet) => wallet.current_link?.plaid_id === plaidId).length;
  };

  const refresh = async () => {
    await dispatch(fetchPlaidWealthSnapshot({ range: chartRange })).unwrap();
  };

  // Unlinking deletes the whole Plaid connection, so every account that shares
  // this access token goes with it.
  const connectionAccounts = (account: BankAccountData) =>
    account.access_token
      ? accounts.filter((other) => !isApex(other) && other.access_token === account.access_token)
      : [account];

  const unlink = (account: BankAccountData) => {
    const title = accountTitle(account);
    const removed = connectionAccounts(account);
    const funding = removed
      .map((other) => ({ title: accountTitle(other), count: fundedCount(other) }))
      .filter((entry) => entry.count > 0);
    if (funding.length > 0) {
      const funded = funding.reduce((sum, entry) => sum + entry.count, 0);
      const fundingTitles = funding.map((entry) => entry.title).join(', ');
      Modal.warning({
        title: 'Pick a new funding bank first',
        content: `${fundingTitles} ${funding.length === 1 ? 'funds' : 'fund'} ${funded} ${funded === 1 ? 'account' : 'accounts'}${removed.length > 1 ? ` and would be unlinked along with ${title}` : ''}. Pick a new funding bank for them first.`,
      });
      return;
    }
    const others = removed.filter((other) => other.account_id !== account.account_id);
    Modal.confirm({
      title: `Unlink ${title}?`,
      content:
        others.length > 0
          ? `This also unlinks ${others.map(accountTitle).join(', ')}, because they're connected through the same bank login.`
          : undefined,
      okText: 'Unlink',
      okButtonProps: { danger: true },
      onOk: async () => {
        setPendingId(account.account_id);
        try {
          await deletePlaidConnection(account.access_token);
        } catch (error) {
          console.error('Failed to unlink Plaid connection', error);
          message.error(`We couldn't unlink ${title}. Please try again.`);
          setPendingId(null);
          return;
        }
        // The unlink already succeeded; a failed reload must not report it as failed.
        message.success(`Unlinked ${title}`);
        try {
          await refresh();
        } catch (error) {
          console.error('Failed to refresh balances after unlink', error);
          message.warning('Unlinked. Your balances will update on the next refresh.');
        } finally {
          setPendingId(null);
        }
      },
    });
  };

  const menu = (account: BankAccountData) => ({
    items: [
      {
        key: 'refresh',
        label: 'Refresh balance',
        onClick: () => {
          refresh().catch((error) => {
            console.error('Failed to refresh linked account balances', error);
            message.error("We couldn't refresh your balances. Try again.");
          });
        },
      },
      {
        key: 'details',
        label: 'View details',
        onClick: () =>
          Modal.info({
            title: accountTitle(account),
            content: `${formatAccountType(account.account_type) || 'Account'}${
              account.mask ? ` ••${account.mask}` : ''
            } · ${formatCurrency(selectPlaidDisplayBalance(account))}`,
          }),
      },
      {
        key: 'unlink',
        label: 'Unlink',
        danger: true,
        disabled: pendingId === account.account_id,
        onClick: () => unlink(account),
      },
    ],
  });

  const row = (account: BankAccountData, kind: 'cash' | 'liability'): LinkedAccountRow => {
    const title = accountTitle(account);
    const accountType = formatAccountType(account.account_type);
    const funded = fundedCount(account);
    return {
      id: account.account_id,
      title: (
        <Tooltip title={title}>
          <span>{title}</span>
        </Tooltip>
      ),
      mark:
        kind === 'cash' ? (
          <BankMark logo={account.institution_logo} name={account.institution_name || title} />
        ) : (
          <LiabilityMark />
        ),
      type: (
        <>
          {kind === 'cash' && (
            <CashTypeIcon savings={fundingChipAccountKind(accountType) === 'savings'} />
          )}
          {linkedAccountTypeLabel(accountType, account.mask)}
        </>
      ),
      amount: formatCurrency(selectPlaidDisplayBalance(account)),
      funds: kind === 'cash' ? linkedAccountFundsLabel(funded) : undefined,
      status: <ConnectedStatus />,
      actions: (
        <Dropdown menu={menu(account)} trigger={['click']}>
          <button
            type="button"
            aria-label={`Actions for ${title}`}
            className="linked-accounts-more"
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      ),
    };
  };

  const linkButton = (
    <Button type="link" icon={<PlusOutlined />} onClick={requestPlaidLink}>
      Link external account
    </Button>
  );
  const hasAccounts = cash.length > 0 || liabilities.length > 0;
  const totals = linkedAccountsTotals(cashTotal, owedTotal);

  return (
    <LinkedAccountsTable
      netTotal={hasAccounts ? totals.netTotal : undefined}
      cashLine={totals.cashLine}
      cashTotal={totals.cashTotal}
      cashRows={cash.map((account) => row(account, 'cash'))}
      liabilityTotal={totals.liabilityTotal}
      liabilityRows={liabilities.map((account) => row(account, 'liability'))}
      footer={hasAccounts ? linkButton : undefined}
      empty={linkButton}
    />
  );
};

export default LinkedAccountsSection;
